import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation data
const translations = {
  en: {
    // Navigation
    home: 'Home',
    features: 'Features',
    pricing: 'Pricing',
    resources: 'Resources',
    about: 'About',
    blog: 'Blog',
    login: 'Log in',
    getStarted: 'Get Started',
    dashboard: 'Dashboard',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Logout',
    
    // Hero Section
    heroTitle: 'AI-Powered Contract Analysis for Middle Eastern Markets',
    heroSubtitle: 'Reduce review time by 70% while ensuring compliance with local regulations',
    heroDescription: 'Transform your contract review process with Jelani AI - the first AI assistant designed specifically for Middle Eastern legal and project contexts.',
    startFreeTrial: 'Start Free Trial',
    watchDemo: 'Watch Demo',
    tryDemo: 'Try Demo',
    
    // Value Proposition
    valuePropositionTitle: 'Why Jelani AI?',
    valuePropositionSubtitle: 'The First Middle East AI Legal Platform for Construction & EngineeringContracts, and in English and Arabic.',
    fastAccurate: 'Fast & Accurate',
    fastAccurateDesc: 'Review contracts 10x faster with 99% accuracy using advanced AI trained on Middle Eastern legal frameworks.',
    localCompliance: 'Local Compliance',
    localComplianceDesc: 'Ensure compliance with GCC, UAE, Saudi, and other regional regulations and standards.',
    multilingual: 'Multilingual',
    multilingualDesc: 'Seamlessly analyze contracts in Arabic and English with cultural context understanding.',
    
    // Features Section
    featuresTitle: 'Jelani AI Features',
    featuresSubtitle: 'Reduce Cost and Time with AI-Powered Contract Analysis',
    featuresDescription: 'Comprehensive contract analysis tailored for Middle Eastern legal and project contexts',
    
    // Feature items
    smartRiskIdentification: 'Smart Risk Identification',
    deviationIdentification: 'Deviation Identification',
    complianceTracking: 'Compliance & Obligation Tracking',
    clauseSummarization: 'Clause Summarization & Extraction',
    multiLanguageSupport: 'Multi-Language Support',
    fidicAnalysis: 'FIDIC & Standard Form Analysis',
    playbookCustomization: 'Playbook Customization',
    
    // How It Works
    howItWorksTitle: 'How It Works',
    howItWorksSubtitle: 'Simple 3-step process to transform your contract review',
    step1Title: 'Upload Contract',
    step1Desc: 'Upload your contract in PDF or Word format',
    step2Title: 'AI Analysis',
    step2Desc: 'Our AI analyzes risks, compliance, and key clauses',
    step3Title: 'Get Results',
    step3Desc: 'Receive detailed insights and recommendations',
    
    // Testimonials
    testimonialsTitle: 'What Our Clients Say',
    testimonialsSubtitle: 'Trusted by leading companies across the Middle East',
    
    // Demo Video
    demoVideoTitle: 'See Jelani AI in Action',
    demoVideoSubtitle: 'Watch how AI transforms contract review',
    
    // CTA Section
    ctaTitle: 'Ready to Transform Your Construction Contract Review?',
    ctaSubtitle: 'Join hundreds of construction professionals who save 95% on legal costs with Jelani AI\'s specialized platform.',
    ctaButton: 'Get Started Now',
    
    // User types
    itsForYou: 'It\'s for You if You Are',
    userTypesSubtitle: 'Appoint your new AI employee to accelerate review, manage risks, and ensure compliance',
    consultants: 'Consultants',
    generalContractors: 'General Contractors',
    developers: 'Developers/Owners',
    legalDepartments: 'Legal Departments',
    projectManagers: 'Project Managers',
    
    // Success stories
    successStories: 'Success Stories',
    successStoriesSubtitle: 'See how Jelani AI has transformed contract management for our clients',
    
    // Footer
    connectWithUs: 'Connect with Us',
    product: 'Product',
    company: 'Company',
    connect: 'Connect',
    allRightsReserved: 'All rights reserved.',
    
    // Dashboard
    totalContracts: 'Total Contracts',
    recentContracts: 'Recent Contracts',
    uploadNewContract: 'Upload New Contract',
    askJelaniAI: 'Ask Jelani AI',
    
    // Chat
    chatWithJelaniAI: 'Chat with Jelani AI',
    askAnything: 'Ask me anything about your contract...',
    
    // Common
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    download: 'Download',
    upload: 'Upload',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    actions: 'Actions',
    analytics: 'Analytics',
    chatAI: 'Chat AI',
    riskLevel: 'Risk Level',
    date: 'Date',
    contract: 'Contract',
    status: 'Status',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed',
    reviewed: 'Reviewed',
    inProgress: 'In Progress',
    
    // Additional Features Page content
    challenge: 'Challenge',
    solution: 'Solution',
    result: 'Result',
    savings: 'Savings',
    infrastructure: 'Infrastructure',
    construction: 'Construction',
    technology: 'Technology',
    watchJelaniAI: 'Watch Jelani AI in Action',
    aiTransformsReview: 'See how AI transforms contract review',
    
    // Pricing Page
    pricingTitle: 'Transparent Pricing',
    pricingSubtitle: 'Choose the perfect plan for your contract management needs',
    monthly: 'Monthly',
    yearly: 'Yearly',
    save17: 'Save 17%',
    mostPopular: 'Most Popular',
    getStarted: 'Get Started',
    contactSales: 'Contact Sales',
    featuresIncluded: 'Features included:',
    limitations: 'Limitations:',
    
    // Plans
    starterPlan: 'Starter',
    professionalPlan: 'Professional',
    enterprisePlan: 'Enterprise',
    starterDesc: 'Perfect for small legal teams and individual consultants',
    professionalDesc: 'Ideal for medium-sized companies and legal departments',
    enterpriseDesc: 'Comprehensive solution for large organizations',
    
    // Usage & Payment
    usageBasedPricing: 'Usage-Based Pricing',
    usageBasedDesc: 'Transparent pricing based on your actual usage and requirements',
    paymentMethods: 'Payment Methods',
    paymentMethodsDesc: 'We support various payment methods for your convenience',
    
    // Service Terms
    serviceTerms: 'Service Terms & Termination',
    whenServicesStop: 'When Services Stop',
    dataRetentionPolicy: 'Data Retention Policy',
    
    // CTA
    readyToStart: 'Ready to Get Started?',
    joinThousands: 'Join thousands of legal professionals who trust Jelani AI for contract management',
    startFreeTrial: 'Start Free Trial',
    
    // About & Resources
    aboutTitle: 'About Jelani AI',
    aboutSubtitle: 'Transforming contract management with AI',
    resourcesTitle: 'Resources',
    resourcesSubtitle: 'Learn how to get the most out of Jelani AI',
    
    // Resources Page
    resourcesPageTitle: 'Resources & Documentation',
    resourcesPageDesc: 'Everything you need to maximize your contract management efficiency',
    legalGuides: 'Legal Guides',
    tutorials: 'Tutorials',
    webinars: 'Webinars',
    
    // Contract Management Pages
    contractChat: 'Contract Chat',
    chatWithContract: 'Chat with Contract',
    mainSummaries: 'Main Summaries',
    articleSummaries: 'Article Summaries',
    deviationRisk: 'Deviation & Risk',
    riskAnalysis: 'Risk Analysis',
    amendedContract: 'Amended Contract',
    viewContract: 'View Contract',
    contractDetails: 'Contract Details',
    backToDashboard: 'Back to Dashboard',
    
    // Contract Actions
    chatAction: 'Chat',
    summariesAction: 'Summaries',
    riskAction: 'Risk',
    amendedAction: 'Amended',
    viewAction: 'View',
    downloadAction: 'Download',
    
    // Contract Chat Page
    askAboutContract: 'Ask about clauses, risks, or FIDIC compliance...',
    quickActions: 'Quick Actions',
    riskAssessment: 'Risk Assessment',
    clauseAnalysis: 'Clause Analysis',
    complianceCheck: 'Compliance Check',
    sendMessage: 'Send Message',
    
    // Summaries Page
    summaryAnalysis: 'Summary Analysis',
    totalSummaries: 'Total Summaries',
    highRiskItems: 'High Risk Items',
    keyInsights: 'Key Insights',
    exportSummaries: 'Export Summaries',
    
    // Risk & Deviation Page
    fidicDeviation: 'FIDIC Deviation',
    riskAssessmentPage: 'Risk Assessment',
    deviationAnalysis: 'Deviation Analysis',
    riskLevel: 'Risk Level',
    totalDeviations: 'Total Deviations',
    criticalRisks: 'Critical Risks',
    
    // Amended Contract Page
    amendmentSummary: 'Amendment Summary',
    totalAmendments: 'Total Amendments',
    approvedAmendments: 'Approved',
    pendingAmendments: 'Pending',
    amendmentDetails: 'Amendment Details',
    originalText: 'Original Text',
    amendedText: 'Amended Text',
    reasonForAmendment: 'Reason for Amendment',
    
    // View Contract Page
    contractOverview: 'Overview',
    documentViewer: 'Document Viewer',
    contractParties: 'Parties',
    contractTimeline: 'Timeline',
    contractValue: 'Contract Value',
    duration: 'Duration',
    complianceScore: 'Compliance Score',
    
    // File Upload
    uploadContract: 'Upload Contract',
    dragDropContract: 'Drag and drop your contract here, or click to browse',
    supportedFormats: 'Supports PDF, DOCX, DOC, TXT files - Unlimited file size',
    uploadProgress: 'Upload Progress',
    filesUploaded: 'Files uploaded successfully!',
    
    // Dashboard Stats
    costSaved: 'Cost Saved',
    totalContracts: 'Total Contracts',
    templates: 'Templates',
    apiDocumentation: 'API Documentation',
    supportCenter: 'Support Center',
    downloadNow: 'Download Now',
    watchNow: 'Watch Now',
    viewGuide: 'View Guide',
    getStartedGuide: 'Get Started Guide',
    
    // About Page
    aboutPageTitle: 'About Jelani AI',
    aboutPageDesc: 'Revolutionizing contract management with AI-powered solutions',
    ourMission: 'Our Mission',
    ourVision: 'Our Vision',
    ourTeam: 'Our Team',
    ourValues: 'Our Values',
    contactUs: 'Contact Us'
  },
  ar: {
    // Navigation
    home: 'الرئيسية',
    features: 'الميزات',
    pricing: 'الأسعار',
    resources: 'اسأل',
    about: 'حول',
    blog: 'المدونة',
    login: 'تسجيل الدخول',
    getStarted: 'ابدأ الآن',
    dashboard: 'لوحة التحكم',
    settings: 'الإعدادات',
    profile: 'الملف الشخصي',
    logout: 'تسجيل الخروج',
    
    // Hero Section
    heroTitle: 'تحليل العقود المدعوم بالذكاء الاصطناعي لأسواق الشرق الأوسط',
    heroSubtitle: 'قلل وقت المراجعة بنسبة 70% مع ضمان الامتثال للوائح المحلية',
    heroDescription: 'حول عملية مراجعة العقود مع الجيلاني الذكي - أول مساعد ذكي مصمم خصيصاً للسياقات القانونية والمشاريع في الشرق الأوسط.',
    startFreeTrial: 'ابدأ التجربة المجانية',
    watchDemo: 'شاهد العرض التوضيحي',
    tryDemo: 'جرب العرض التوضيحي',
    
    // Value Proposition
    valuePropositionTitle: 'لماذا الجيلاني الذكي؟',
    valuePropositionSubtitle: 'أول منصة قانونية في الشرق الأوسط لعقود البناء والهندسة، باللغتين الإنجليزية والعربية',
    fastAccurate: 'سريع ودقيق',
    fastAccurateDesc: 'راجع العقود أسرع بـ 10 مرات مع دقة 99% باستخدام الذكاء الاصطناعي المتطور المدرب على الأطر القانونية في الشرق الأوسط.',
    localCompliance: 'الامتثال المحلي',
    localComplianceDesc: 'ضمان الامتثال للوائح ومعايير دول مجلس التعاون الخليجي والإمارات والسعودية وغيرها من المناطق.',
    multilingual: 'متعدد اللغات',
    multilingualDesc: 'تحليل سلس للعقود بالعربية والإنجليزية مع فهم السياق الثقافي.',
    
    // Features Section
    featuresTitle: 'ميزات الجيلاني الذكي',
    featuresSubtitle: 'قلل التكلفة والوقت مع تحليل العقود المدعوم بالذكاء الاصطناعي',
    featuresDescription: 'تحليل شامل للعقود مصمم خصيصاً للسياقات القانونية والمشاريع في الشرق الأوسط',
    
    // Feature items
    smartRiskIdentification: 'تحديد المخاطر الذكي',
    deviationIdentification: 'تحديد الانحرافات',
    complianceTracking: 'تتبع الامتثال والالتزامات',
    clauseSummarization: 'تلخيص واستخراج البنود',
    multiLanguageSupport: 'دعم متعدد اللغات',
    fidicAnalysis: 'تحليل FIDIC والنماذج المعيارية',
    playbookCustomization: 'تخصيص دليل العمل',
    
    // How It Works
    howItWorksTitle: 'كيف يعمل',
    howItWorksSubtitle: 'عملية بسيطة من 3 خطوات لتحويل مراجعة العقود',
    step1Title: 'رفع العقد',
    step1Desc: 'ارفع عقدك بتنسيق PDF أو Word',
    step2Title: 'تحليل الذكاء الاصطناعي',
    step2Desc: 'يحلل الذكاء الاصطناعي المخاطر والامتثال والبنود الرئيسية',
    step3Title: 'احصل على النتائج',
    step3Desc: 'احصل على رؤى مفصلة وتوصيات',
    
    // Testimonials
    testimonialsTitle: 'ما يقوله عملاؤنا',
    testimonialsSubtitle: 'موثوق به من قبل الشركات الرائدة في الشرق الأوسط',
    
    // Demo Video
    demoVideoTitle: 'شاهد الجيلاني الذكي في العمل',
    demoVideoSubtitle: 'شاهد كيف يحول الذكاء الاصطناعي مراجعة العقود',
    
    // CTA Section
    ctaTitle: 'هل أنت مستعد لتحويل مراجعة عقود البناء؟',
    ctaSubtitle: 'انضم إلى مئات المهنيين في مجال البناء الذين يوفرون 95% من التكاليف القانونية مع منصة الجيلاني الذكي المتخصصة.',
    ctaButton: 'ابدأ الآن',
    
    // User types
    itsForYou: 'إنه لك إذا كنت',
    userTypesSubtitle: 'عين موظفك الذكي الجديد لتسريع المراجعة وإدارة المخاطر وضمان الامتثال',
    consultants: 'استشاريون',
    generalContractors: 'مقاولون عامون',
    developers: 'مطورون/مالكون',
    legalDepartments: 'الأقسام القانونية',
    projectManagers: 'مديرو المشاريع',
    
    // Success stories
    successStories: 'قصص نجاح',
    successStoriesSubtitle: 'اطلع على كيف غيّر الجيلاني الذكي إدارة العقود لعملائنا',
    
    // Footer
    connectWithUs: 'تواصل معنا',
    product: 'المنتج',
    company: 'الشركة',
    connect: 'تواصل',
    allRightsReserved: 'جميع الحقوق محفوظة.',
    
    // Dashboard
    totalContracts: 'إجمالي العقود',
    recentContracts: 'العقود الحديثة',
    uploadNewContract: 'رفع عقد جديد',
    askJelaniAI: 'اسأل الجيلاني الذكي',
    
    // Chat
    chatWithJelaniAI: 'تحدث مع الجيلاني الذكي',
    askAnything: 'اسألني أي شيء عن عقدك...',
    
    // Common
    loading: 'جاري التحميل...',
    error: 'خطأ',
    success: 'نجح',
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    view: 'عرض',
    download: 'تحميل',
    upload: 'رفع',
    search: 'بحث',
    filter: 'تصفية',
    sort: 'ترتيب',
    actions: 'إجراءات',
    analytics: 'تحليلات',
    chatAI: 'الذكاء الاصطناعي',
    riskLevel: 'مستوى المخاطر',
    date: 'التاريخ',
    contract: 'العقد',
    status: 'الحالة',
    low: 'منخفض',
    medium: 'متوسط',
    high: 'عالي',
    pending: 'قيد الانتظار',
    processing: 'قيد المعالجة',
    completed: 'مكتمل',
    reviewed: 'تمت المراجعة',
    inProgress: 'قيد التنفيذ',
    
    // Additional Features Page content
    challenge: 'التحدي',
    solution: 'الحل',
    result: 'النتيجة',
    savings: 'التوفير',
    infrastructure: 'البنية التحتية',
    construction: 'البناء',
    technology: 'التكنولوجيا',
    watchJelaniAI: 'شاهد الجيلاني الذكي في العمل',
    aiTransformsReview: 'اطلع على كيف يحول الذكاء الاصطناعي مراجعة العقود',
    
    // Pricing Page
    pricingTitle: 'أسعار شفافة',
    pricingSubtitle: 'اختر الخطة المثالية لاحتياجات إدارة العقود',
    monthly: 'شهري',
    yearly: 'سنوي',
    save17: 'وفر 17%',
    mostPopular: 'الأكثر شعبية',
    getStarted: 'ابدأ الآن',
    contactSales: 'اتصل بالمبيعات',
    featuresIncluded: 'الميزات المشمولة:',
    limitations: 'القيود:',
    
    // Plans
    starterPlan: 'المبتدئ',
    professionalPlan: 'المحترف',
    enterprisePlan: 'المؤسسي',
    starterDesc: 'مثالي للفرق القانونية الصغيرة والاستشاريين الأفراد',
    professionalDesc: 'مناسب للشركات متوسطة الحجم والأقسام القانونية',
    enterpriseDesc: 'حل شامل للمؤسسات الكبيرة',
    
    // Usage & Payment
    usageBasedPricing: 'التسعير حسب الاستخدام',
    usageBasedDesc: 'تسعير شفاف بناءً على استخدامك الفعلي ومتطلباتك',
    paymentMethods: 'طرق الدفع',
    paymentMethodsDesc: 'ندعم طرق دفع متنوعة لراحتك',
    
    // Service Terms
    serviceTerms: 'شروط الخدمة والإنهاء',
    whenServicesStop: 'عندما تتوقف الخدمات',
    dataRetentionPolicy: 'سياسة الاحتفاظ بالبيانات',
    
    // CTA
    readyToStart: 'مستعد للبدء؟',
    joinThousands: 'انضم إلى آلاف المهنيين القانونيين الذين يثقون بالجيلاني الذكي لإدارة العقود',
    startFreeTrial: 'ابدأ التجربة المجانية',
    
    // About & Resources
    aboutTitle: 'حول الجيلاني الذكي',
    aboutSubtitle: 'تحويل إدارة العقود بالذكاء الاصطناعي',
    resourcesTitle: 'الموارد',
    resourcesSubtitle: 'تعلم كيفية الاستفادة القصوى من الجيلاني الذكي',
    
    // Resources Page
    resourcesPageTitle: 'الموارد والوثائق',
    resourcesPageDesc: 'كل ما تحتاجه لزيادة كفاءة إدارة العقود إلى أقصى حد',
    legalGuides: 'الأدلة القانونية',
    tutorials: 'الدروس التعليمية',
    webinars: 'الندوات عبر الإنترنت',
    
    // Contract Management Pages
    contractChat: 'محادثة العقد',
    chatWithContract: 'تحدث مع العقد',
    mainSummaries: 'الملخصات الرئيسية',
    articleSummaries: 'ملخصات المقالات',
    deviationRisk: 'الانحراف والمخاطر',
    riskAnalysis: 'تحليل المخاطر',
    amendedContract: 'العقد المعدل',
    viewContract: 'عرض العقد',
    contractDetails: 'تفاصيل العقد',
    backToDashboard: 'العودة إلى لوحة التحكم',
    
    // Contract Actions
    chatAction: 'محادثة',
    summariesAction: 'ملخصات',
    riskAction: 'مخاطر',
    amendedAction: 'معدل',
    viewAction: 'عرض',
    downloadAction: 'تحميل',
    
    // Contract Chat Page
    askAboutContract: 'اسأل عن البنود أو المخاطر أو امتثال FIDIC...',
    quickActions: 'إجراءات سريعة',
    riskAssessment: 'تقييم المخاطر',
    clauseAnalysis: 'تحليل البنود',
    complianceCheck: 'فحص الامتثال',
    sendMessage: 'إرسال رسالة',
    
    // Summaries Page
    summaryAnalysis: 'تحليل الملخص',
    totalSummaries: 'إجمالي الملخصات',
    highRiskItems: 'عناصر عالية المخاطر',
    keyInsights: 'رؤى رئيسية',
    exportSummaries: 'تصدير الملخصات',
    
    // Risk & Deviation Page
    fidicDeviation: 'انحراف FIDIC',
    riskAssessmentPage: 'تقييم المخاطر',
    deviationAnalysis: 'تحليل الانحراف',
    riskLevel: 'مستوى المخاطر',
    totalDeviations: 'إجمالي الانحرافات',
    criticalRisks: 'مخاطر حرجة',
    
    // Amended Contract Page
    amendmentSummary: 'ملخص التعديل',
    totalAmendments: 'إجمالي التعديلات',
    approvedAmendments: 'موافق عليه',
    pendingAmendments: 'قيد الانتظار',
    amendmentDetails: 'تفاصيل التعديل',
    originalText: 'النص الأصلي',
    amendedText: 'النص المعدل',
    reasonForAmendment: 'سبب التعديل',
    
    // View Contract Page
    contractOverview: 'نظرة عامة',
    documentViewer: 'عارض المستندات',
    contractParties: 'الأطراف',
    contractTimeline: 'الجدول الزمني',
    contractValue: 'قيمة العقد',
    duration: 'المدة',
    complianceScore: 'نقاط الامتثال',
    
    // File Upload
    uploadContract: 'رفع عقد',
    dragDropContract: 'اسحب وأفلت عقدك هنا، أو انقر للتصفح',
    supportedFormats: 'يدعم ملفات PDF وDOCX وDOC وTXT - حجم غير محدود',
    uploadProgress: 'تقدم الرفع',
    filesUploaded: 'تم رفع الملفات بنجاح!',
    
    // Dashboard Stats
    costSaved: 'التكلفة المحفوظة',
    totalContracts: 'إجمالي العقود',
    templates: 'النماذج',
    apiDocumentation: 'وثائق واجهة برمجة التطبيقات',
    supportCenter: 'مركز الدعم',
    downloadNow: 'تحميل الآن',
    watchNow: 'شاهد الآن',
    viewGuide: 'عرض الدليل',
    getStartedGuide: 'دليل البدء',
    
    // About Page
    aboutPageTitle: 'حول الجيلاني الذكي',
    aboutPageDesc: 'ثورة في إدارة العقود بحلول مدعومة بالذكاء الاصطناعي',
    ourMission: 'مهمتنا',
    ourVision: 'رؤيتنا',
    ourTeam: 'فريقنا',
    ourValues: 'قيمنا',
    contactUs: 'اتصل بنا',
    
    // Resources Page additional
    resourcesSupport: 'الموارد والدعم',
    resourcesDescription: 'كل ما تحتاجه لإتقان تحليل العقود باستخدام الجيلاني الذكي',
    faqTitle: 'الأسئلة الشائعة',
    faqDescription: 'أسئلة شائعة حول الجيلاني الذكي وتحليل العقود',
    supportTitle: 'هل تحتاج إلى دعم إضافي؟',
    supportDescription: 'فريق الخبراء لدينا هنا لمساعدتك في الحصول على أقصى استفادة من الجيلاني الذكي',
    emailSupport: 'الدعم عبر البريد الإلكتروني',
    phoneSupport: 'الدعم الهاتفي',
    liveChat: 'المحادثة المباشرة',
    available24: 'متاح 24/7'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });
  
  const [direction, setDirection] = useState('ltr');

  useEffect(() => {
    localStorage.setItem('language', language);
    setDirection(language === 'ar' ? 'rtl' : 'ltr');
    
    // Update document direction and language
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Apply RTL-specific CSS classes to body
    if (language === 'ar') {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [language]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  const value = {
    language,
    direction,
    t,
    changeLanguage,
    isRTL: language === 'ar'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
